import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import RefreshIcon from "@mui/icons-material/Refresh";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { getHoldReasons, putOnHold, putOffHold } from "requests/onHold";
import { CASE_HOLD_UPDATE } from "typeConstants/rights";
import { MSpinner, MField, MCollapsibleCard } from "components/Shared";
import { DATA_TYPE_DATE, DATA_TYPE_STRING } from "typeConstants/dataTypes";
import { CaseHoldDetailDto } from "interfaces/Mueller.Business.Models";
import {format} from "date-fns";

export const ON_HOLD_TAB_ID = "ON_HOLD_TAB_ID";
export const ON_HOLD_MESSAGE_TAB_ID = "ON_HOLD_MESSAGE_TAB_ID";

const MuellerOnHold = ({
  controlNumber,
  canUpdateOnHold,
  internetUserId,
}) => {
  const [fetchingHoldStatus, setFetchingHoldStatus] = useState(false);
  const [isOnHold, setIsOnHold] = useState(false);
  const [showOnHoldDialog, setShowOnHoldDialog] = useState(false);
  const [offHoldDate, setOffHoldDate] = useState(null);
  const [onHoldNote, setOnHoldNote] = useState(null);
  const mounted = useRef(true);

  useEffect(() => {
    getHoldInfo();
    return () => {
      mounted.current = false;
    };
  }, []);

  const getHoldInfo = () => {
    setFetchingHoldStatus(true);
    getHoldReasons(controlNumber, handleStatus, handleStatusError, mounted);
  };

  const handleStatus = (response: CaseHoldDetailDto | null) => {
    if (response) {
      setIsOnHold(true);
      setOffHoldDate(response.endDate);
      setFetchingHoldStatus(false);
    }
    else {
      // Handle case where response is null
      setIsOnHold(false);
      setOffHoldDate(undefined);
      setFetchingHoldStatus(false);
    }
  };
  const handleStatusError = () => setFetchingHoldStatus(false);

  const handleOnHoldClick = () => {
    if (isOnHold) {
      putOffHold(controlNumber, internetUserId, onHoldChangedCallback, onHoldChangedCallback, mounted);
    } else {
      putOnHold(controlNumber, internetUserId, offHoldDate, onHoldNote, onHoldChangedCallback, onHoldChangedCallback, mounted);
    }
  };

  const onHoldChangedCallback = () => {
    setFetchingHoldStatus(true);
    getHoldInfo();
  };

  const sendOnHoldActions = [
    <Button
      className="m-t-10"
      key="continue"
      onClick={() => {
        setShowOnHoldDialog(false);
        handleOnHoldClick();
      }}
    >
      Continue
    </Button>,
    <Button
      className="m-t-10"
      key="cancel"
      onClick={() => setShowOnHoldDialog(false)}
    >
      Cancel
    </Button>
  ];

  if (fetchingHoldStatus) {
    return <MSpinner />;
  }

  const statusContent = (
    <h4>
      <b>Status: </b> {`${isOnHold ? "On Hold till " + (offHoldDate ? format(new Date(offHoldDate), "MM/dd/yyyy") : "[Unknown]") : "Off Hold"} `}
      <IconButton size="small" title="Refresh Status" onClick={getHoldInfo}>
        <RefreshIcon />
      </IconButton>
    </h4>
  );

  return (
    <>
      <MCollapsibleCard
        title="Mueller On Hold Actions"
        className="md-cell md-cell--12 case-header"
        persistentContent={statusContent}
      >
        <span className={"case-action"}>
          <Button
            id="on-hold-action-button"
            onClick={() => setShowOnHoldDialog(true)}
            disabled={!canUpdateOnHold || isOnHold}
          >
            On Hold
          </Button>
        </span>
        <span className={"case-action"}>
          <Button
            id="off-hold-action-button"
            onClick={handleOnHoldClick}
            disabled={!canUpdateOnHold || !isOnHold}
          >
            Off Hold
          </Button>
        </span>
      </MCollapsibleCard>
      <div id="off-hold-date-container">
        <Dialog
          id="dialog-off-hold-date"
          className="card-dialog dialog-off-hold-date"
          open={showOnHoldDialog}
          sx={{ '& .MuiDialog-paper': { width: 500 } }}
        >
          <DialogTitle className="card-dialog-title">Set Case On-Hold</DialogTitle>
          <DialogContent dividers className="dialog-off-hold-content">
            <div>Set Off-Hold Date? (optional)</div>
            <MField
              key={"update-off-hold-date"}
              id={"update-off-hold-date"}
              dataType={DATA_TYPE_DATE}
              onChange={(_, i) => setOffHoldDate(i)}
            />
            <div className="m-t-10">Set On-Hold Note (optional)</div>          
            <MField            
              key={"update-on-hold-note"}
              id={"update-on-hold-note"}
              dataType={DATA_TYPE_STRING}
              onChange={(_, i) => setOnHoldNote(i)}
              className="full-width-field"
            />
          </DialogContent>
          <DialogActions>
            {sendOnHoldActions}
          </DialogActions>
        </Dialog>
      </div>
    </>
  );
};

MuellerOnHold.propTypes = {
  controlNumber: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  caseHeader: PropTypes.object,
  canUpdateOnHold: PropTypes.bool,
  onHoldReasons: PropTypes.array,
  isFetchingHoldReasons: PropTypes.bool,
  openCaseActionTab: PropTypes.func, // Tab control method used to open a new tab
  closeTab: PropTypes.func,  // Tab control method that is called with a tab id to close the tab
  internetUserId: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.number
  ]),
};

const mapStateToProps = (state) => {
  return {
    controlNumber: state.caseDetails.controlNumber,
    caseHeader: state.caseForms.caseHeader,
    canUpdateOnHold: state.user.profile.rights.includes(CASE_HOLD_UPDATE),
    internetUserId: state.user.profile.internetUserId,
  };
};

export default connect(mapStateToProps)(MuellerOnHold);
